import * as React from "react";
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Stack,
  Paper,
} from "@mui/material";
import { useI18n } from "../../i18n/I18nProvider";
import { supabase } from "../../logic/client";


export default function BlackoutKnowledgeQuestion(props: {
  handlePageChange: (page: string) => void;
}) {
  const { lang } = useI18n();
  const L = (en: string, de: string) => (lang === "de" ? de : en);

  const [selected, setSelected] = React.useState<string[]>([]);
  const [saving, setSaving] = React.useState(false);

  const options = [
    {
      key: "lighting_heating",
      en: "Lighting & heating: All lamps and central heating systems",
      de: "Licht & Wärme: Sämtliche Lampen und die Zentralheizung",
    },
    {
      key: "kitchen_household",
      en: "Kitchen & household: Stove, oven, fridge, freezer, etc.",
      de: "Küche & Haushalt: Herd, Backofen, Kühlschrank und Tiefkühler",
    },
    {
      key: "communication_media",
      en: "Communication & media: WiFi, internet, TV, radio",
      de: "Kommunikation & Medien: WLAN, Internet, Fernseher, Radio",
    },
    {
      key: "water_sanitation",
      en: "Water & sanitation: Pumps and electric flushing systems",
      de: "Wasser & Sanitär: Wasserpumpen und elektrische Spülung",
    },
    {
      key: "other_household",
      en: "Other: Washing machine, vacuum, shutters, PC systems",
      de: "Sonstiges: Waschmaschine, Staubsauger, Rollläden, PC-Systeme",
    },
  ];

  function toggle(key: string) {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((v) => v !== key) : [...prev, key]
    );
  }

  async function saveAnswers() {
    setSaving(true);

    const { data: userRes } = await supabase.auth.getUser();

    const { data: emp } = await supabase
      .from("employees")
      .select("id")
      .eq("auth_user_id", userRes?.user?.id)
      .single();

    const { data: workshop } = await supabase
      .from("workshops")
      .select("id")
      .eq("is_active", true)
      .single();

    if (!emp || !workshop) {
      console.error("Missing employee/workshop");
      setSaving(false);
      return;
    }

    await supabase
      .from("workshop_participant_blackout_knowledge")
      .delete()
      .eq("workshop_id", workshop.id)
      .eq("employee_id", emp.id);

    const rows = options
      .filter((opt) => selected.includes(opt.key))
      .map((opt) => ({
        workshop_id: workshop.id,
        employee_id: emp.id,
        option_key: opt.key,
        option_en: opt.en,
        option_de: opt.de,
        is_correct: true,
      }));

    const { error } = await supabase
      .from("workshop_participant_blackout_knowledge")
      .insert(rows);

    setSaving(false);

    if (error) {
      console.error("SAVE BLACKOUT ANSWERS ERROR:", error);
      return;
    }

    props.handlePageChange("AwarenessIntro");
  }

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 48px)",
        width: "100%",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 6 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `
          radial-gradient(circle at 20% 20%, rgba(251, 2, 2, 0.08), transparent 40%),
          radial-gradient(circle at 80% 30%, rgba(255, 0, 0, 0.06), transparent 40%),
          #111010
        `,
        backgroundImage: `
          linear-gradient(
            rgba(8, 8, 8, 0.78),
            rgba(8, 8, 8, 0.82)
          ),
          url("/scenarioBgs/blackout.png")
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <Paper
        sx={{
          width: { xs: "100%", sm: "min(88vw, 780px)", md: "min(78vw, 860px)" },
          borderRadius: 5,
          p: { xs: 3, sm: 5, md: 6 },
          border: "1px solid rgba(255,255,255,0.14)",
          background:
            "linear-gradient(180deg, rgba(49, 32, 32, 0.95), rgba(34, 21, 21, 0.94))",
          boxShadow: "0 24px 65px rgba(0,0,0,0.45)",
        }}
      >

        {/* QUESTION */}
        <Typography
          variant="h4"
          sx={{
            mb: 4,
            color: "#fff",
            fontSize: { xs: "1.4rem", sm: "1.7rem", md: "1.9rem" },
            fontWeight: 700,
            lineHeight: 1.25,
          }}
        >
          {L(
            "What stops working during a blackout? Select all that apply.",
            "Was funktioniert bei einem Stromausfall nicht mehr? Wähle die richtigen Antworten."
          )}
        </Typography>

        {/* OPTIONS (MULTI SELECT) */}
        <Stack>
          {options.map((opt) => {
            const label = L(opt.en, opt.de);
            const checked = selected.includes(opt.key);

            return (
              <FormControlLabel
                key={opt.key}
                control={
                  <Checkbox
                    checked={checked}
                    onChange={() => toggle(opt.key)}
                    sx={{
                      color: "rgba(255,255,255,0.5)",
                      "&.Mui-checked": { color: "#f05969" },
                    }}
                  />
                }
                label={label}
                sx={{
                  color: "#fff",
                  mx: 0,
                  px: 2,
                  py: 1.25,
                  borderRadius: 2.5,
                  mb: 1.25,
                  border: "1px solid rgba(255,255,255,0.18)",
                  backgroundColor: "rgba(255,255,255,0.04)",
                  transition: "all .2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.09)",
                    borderColor: "rgba(255,255,255,0.34)",
                  },
                }}
              />
            );
          })}
        </Stack>

        {/* ACTIONS */}
        <Stack direction="row" spacing={2} sx={{ mt: 5 }}>
          <Button
            variant="outlined"
            onClick={() => props.handlePageChange("ControlPanel")}
            sx={{
              borderColor: "rgba(255,255,255,0.4)",
              color: "#fff",
              px: 3,
            }}
          >
            {L("Back", "Zurück")}
          </Button>

          <Button
            variant="contained"
            onClick={saveAnswers}
            disabled={selected.length === 0 || saving}
            sx={{
              px: 3,
              background: "linear-gradient(90deg, #b50d20, #d6293e)",
              fontWeight: 700,
              "&.Mui-disabled": {
                background: "rgba(160,160,160,0.35)",
                color: "rgba(255,255,255,0.55)",
              },
            }}
          >
            {saving ? L("Saving...", "Speichern...") : L("Continue", "Weiter")}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}